import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Button, Spinner, Alert } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import { API_URL } from '../../config/api';
import CurrencyFormatter from '../../utils/CurrencyFormatter';
import './SaleCheckoutPaymentPage.css';

/**
 * 💳 SALE CHECKOUT PAYMENT PAGE - Pagamento de venda existente
 *
 * Usado quando o cliente recebe email de cobrança (Afterpay) e precisa pagar.
 * A venda já existe no banco - esta página apenas processa o pagamento real
 * via Pagar.me (PIX, Boleto, Cartão de Crédito).
 */
const SaleCheckoutPaymentPage = () => {
  const { saleId } = useParams();
  const navigate = useNavigate();

  // Estados principais
  const [loading, setLoading] = useState(true);
  const [sale, setSale] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('pix');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [installments, setInstallments] = useState(1);

  // Estados para tokenização de cartão
  const [pagarmeLoaded, setPagarmeLoaded] = useState(false);
  const [pagarmePublicKey, setPagarmePublicKey] = useState(null);

  // Estados para modais de PIX e Boleto
  const [showPixModal, setShowPixModal] = useState(false);
  const [pixData, setPixData] = useState(null);
  const [pixCopied, setPixCopied] = useState(false);
  const [showBoletoModal, setShowBoletoModal] = useState(false);
  const [boletoData, setBoletoData] = useState(null);
  const [boletoCopied, setBoletoCopied] = useState(false);

  // Carregar dados da venda
  useEffect(() => {
    loadSaleData();
  }, [saleId]);

  // Carregar tokenizecard.js com public key
  useEffect(() => {
    const loadPagarmeScript = async () => {
      if (window.PagarmeCheckout) {
        setPagarmeLoaded(true);
        return;
      }

      try {
        const keyResponse = await fetch(`${API_URL}/api/payments/public-key`);
        if (!keyResponse.ok) return;

        const keyData = await keyResponse.json();
        const publicKey = keyData.public_key;
        if (!publicKey) return;

        setPagarmePublicKey(publicKey);

        const script = document.createElement('script');
        script.src = 'https://checkout.pagar.me/v1/tokenizecard.js';
        script.setAttribute('data-pagarmecheckout-app-id', publicKey);

        const postbackUrl = window.location.href.split('?')[0].split('#')[0];
        script.setAttribute('data-pagarmecheckout-postback-url', postbackUrl);

        script.onload = () => {
          setPagarmeLoaded(true);
        };
        script.onerror = () => {
          console.error('Erro ao carregar tokenizecard.js');
        };
        document.body.appendChild(script);
      } catch (err) {
        console.error('Erro ao configurar pagamento:', err);
      }
    };

    loadPagarmeScript();
  }, []);

  // Polling PIX - verificar pagamento a cada 5 segundos
  useEffect(() => {
    if (!showPixModal || !pixData?.sale_id) return;

    const checkPaymentStatus = async () => {
      try {
        const response = await fetch(`${API_URL}/api/sales/${pixData.sale_id}/payment-status`);
        if (!response.ok) return;

        const result = await response.json();
        const status = result.data?.status;

        if (status === 'paid') {
          setShowPixModal(false);
          navigate('/checkout/success', {
            state: {
              order: result.data
            }
          });
        }
      } catch (err) {
        console.error('Erro ao verificar status:', err);
      }
    };

    checkPaymentStatus();
    const interval = setInterval(checkPaymentStatus, 5000);
    return () => clearInterval(interval);
  }, [showPixModal, pixData?.sale_id, navigate, sale]);

  // Detectar token do Pagar.me na URL (após redirect da tokenização de cartão)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const pagarmeToken = urlParams.get('pagarmetoken-0') || urlParams.get('pagarmetoken');

    // Evitar loop: verificar se já processamos este token
    const processedToken = sessionStorage.getItem('processed-sale-token');
    if (processedToken === pagarmeToken) return;

    if (!pagarmeToken || !saleId) return;

    console.log('Token Pagar.me detectado na URL:', pagarmeToken);
    sessionStorage.setItem('processed-sale-token', pagarmeToken);

    const savedInstallments = parseInt(localStorage.getItem('checkout-sale-installments') || '1');

    setProcessing(true);
    setError('');

    (async () => {
      try {
        const response = await fetch(`${API_URL}/api/sales/${saleId}/process-payment`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            payment_method: 'credit_card',
            card_token: pagarmeToken,
            installments: savedInstallments
          })
        });

        const result = await response.json();
        console.log('Resposta do backend (cartão via token):', result);

        // Limpar localStorage
        localStorage.removeItem('checkout-sale-id');
        localStorage.removeItem('checkout-sale-installments');

        if (result.success) {
          navigate('/checkout/success', {
            state: {
              order: result.data,
              payment_info: result.data.payment_info
            }
          });
        } else {
          setError(result.error || 'Erro ao processar pagamento com cartão');
          setProcessing(false);
        }
      } catch (err) {
        console.error('Erro ao processar token:', err);
        setError('Erro ao processar pagamento. Tente novamente.');
        setProcessing(false);
      }
    })();
  }, [saleId, navigate]);

  const loadSaleData = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/sales/${saleId}/checkout-details`);

      if (!response.ok) {
        throw new Error('Venda não encontrada ou link expirado');
      }

      const data = await response.json();

      let saleData = data.data;

      if (data.data.available_payment_methods) {
        const methods = data.data.available_payment_methods;

        const availableMethods = {
          pix: methods.pix || false,
          credit_card: methods.credit_card || methods.cartao || false,
          boleto: methods.boleto || false
        };

        if (availableMethods.pix) setPaymentMethod('pix');
        else if (availableMethods.credit_card) setPaymentMethod('credit_card');
        else if (availableMethods.boleto) setPaymentMethod('boleto');

        saleData = { ...data.data, available_payment_methods: availableMethods };
      }

      setSale(saleData);

    } catch (err) {
      console.error('Erro ao carregar venda:', err);
      setError(err.message || 'Erro ao carregar dados da venda');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Processar pagamento real
   */
  const handlePayment = async () => {
    setProcessing(true);
    setError('');

    try {
      // Cartão de crédito: tokenizar via Pagar.me (redirect)
      if (paymentMethod === 'credit_card') {
        if (!window.PagarmeCheckout || !window.PagarmeCheckout.init) {
          throw new Error('Sistema de pagamento não carregado. Aguarde um momento e tente novamente.');
        }

        const cardNumber = document.querySelector('[data-pagarmecheckout-element="number"]')?.value;
        const holderName = document.querySelector('[data-pagarmecheckout-element="holder_name"]')?.value;
        const expMonth = document.querySelector('[data-pagarmecheckout-element="exp_month"]')?.value;
        const expYear = document.querySelector('[data-pagarmecheckout-element="exp_year"]')?.value;
        const cvv = document.querySelector('[data-pagarmecheckout-element="cvv"]')?.value;

        if (!cardNumber || !holderName || !expMonth || !expYear || !cvv) {
          throw new Error('Por favor, preencha todos os dados do cartão');
        }

        // Salvar dados no localStorage para recuperar após redirect
        localStorage.setItem('checkout-sale-id', saleId);
        localStorage.setItem('checkout-sale-installments', installments.toString());

        // Configurar callbacks e acionar tokenização via form submit
        window.PagarmeCheckout.init(
          function success(data) {
            console.log('Token gerado, Pagar.me vai redirecionar...');
          },
          function fail(error) {
            console.error('Erro na tokenização:', error);
            setError('Erro ao processar dados do cartão. Tente novamente.');
            setProcessing(false);
            localStorage.removeItem('checkout-sale-id');
            localStorage.removeItem('checkout-sale-installments');
          }
        );

        // Simular submit do form para acionar o tokenizecard.js
        setTimeout(() => {
          const form = document.querySelector('[data-pagarmecheckout-form]');
          if (form) {
            console.log('Disparando submit para tokenização...');
            const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
            form.dispatchEvent(submitEvent);
          } else {
            console.error('Form [data-pagarmecheckout-form] não encontrado');
            setError('Erro interno. Recarregue a página e tente novamente.');
            setProcessing(false);
          }
        }, 500);
        return;
      }

      // PIX e Boleto: chamada direta ao backend
      const response = await fetch(`${API_URL}/api/sales/${saleId}/process-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ payment_method: paymentMethod })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erro ao processar pagamento');
      }

      // PIX: mostrar QR code modal
      if (paymentMethod === 'pix' && data.data?.payment_info?.qr_code) {
        setPixData({
          qr_code: data.data.payment_info.qr_code,
          qr_code_url: data.data.payment_info.qr_code_url,
          expires_at: data.data.payment_info.expires_at,
          sale_id: data.data.id,
          order_id: data.data.payment_order_id,
          total: data.data.total_amount
        });
        setShowPixModal(true);
        setProcessing(false);
        return;
      }

      // Boleto: mostrar barcode modal
      if (paymentMethod === 'boleto' && data.data?.payment_info?.barcode) {
        setBoletoData({
          barcode: data.data.payment_info.barcode,
          url: data.data.payment_info.url,
          due_date: data.data.payment_info.due_date,
          instructions: data.data.payment_info.instructions,
          sale_id: data.data.id
        });
        setShowBoletoModal(true);
        setProcessing(false);
        return;
      }

      // Fallback: redirecionar para sucesso
      navigate('/checkout/success', { state: { order: data.data } });

    } catch (err) {
      console.error('Erro ao processar pagamento:', err);
      setError(err.message || 'Erro ao processar pagamento');
    } finally {
      setProcessing(false);
    }
  };

  const handleCopyPix = async () => {
    try {
      await navigator.clipboard.writeText(pixData.qr_code);
      setPixCopied(true);
      setTimeout(() => setPixCopied(false), 3000);
    } catch (err) {
      console.error('Erro ao copiar:', err);
    }
  };

  const handleCopyBoleto = async () => {
    try {
      await navigator.clipboard.writeText(boletoData.barcode);
      setBoletoCopied(true);
      setTimeout(() => setBoletoCopied(false), 3000);
    } catch (err) {
      console.error('Erro ao copiar:', err);
    }
  };

  const maxInstallments = sale?.max_installments || 12;

  // Loading
  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Carregando...</p>
      </Container>
    );
  }

  // Erro fatal
  if (error && !sale) {
    return (
      <Container className="py-5">
        <Alert variant="danger">
          <Icon icon="mdi:alert-circle" className="me-2" />
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <div className="sale-checkout-payment-page">
      <Container className="py-4">
        <Row className="justify-content-center">
          <Col lg={8}>
            {/* Header */}
            <div className="checkout-header text-center mb-4">
              <Icon icon="mdi:lock-check" style={{ fontSize: '48px', color: '#667eea' }} />
              <span className="d-block mt-3" style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>Finalize seu Pagamento</span>
              <p className="text-muted">
                Pedido #{saleId} - {sale?.product_name}
              </p>
            </div>

            {error && (
              <Alert variant="danger" className="mb-4" dismissible onClose={() => setError('')}>
                <Icon icon="mdi:alert-circle" className="me-2" />
                {error}
              </Alert>
            )}

            <Row>
              {/* Coluna Esquerda: Dados do Pedido */}
              <Col lg={6} className="mb-4">
                <Card className="shadow-sm h-100">
                  <Card.Header className="bg-light">
                    <Icon icon="mdi:package-variant" className="me-2" />
                    <strong>Dados do Pedido</strong>
                  </Card.Header>
                  <Card.Body>
                    <div className="mb-4">
                      <small className="text-muted">Produto</small>
                      <div className="d-flex align-items-center mt-2">
                        {sale?.product_image && (
                          <img
                            src={sale.product_image}
                            alt={sale.product_name}
                            style={{
                              width: '60px',
                              height: '60px',
                              objectFit: 'cover',
                              borderRadius: '12px',
                              marginRight: '15px',
                              border: '2px solid #e9ecef'
                            }}
                          />
                        )}
                        <div>
                          <strong style={{ fontSize: '1.05rem' }}>{sale?.product_name}</strong>
                          {sale?.is_afterpay && (
                            <div className="mt-1">
                              <span className="badge bg-info" style={{ fontSize: '0.75rem' }}>
                                Pagamento na Entrega
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="mb-3">
                      <small className="text-muted">Cliente</small>
                      <div><strong>{sale?.customer_name}</strong></div>
                      <div className="text-muted">{sale?.customer_email}</div>
                      <div className="text-muted">{sale?.customer_phone}</div>
                    </div>

                    {sale?.shipping_address && (
                      <div className="mb-3">
                        <small className="text-muted">Endereço de Entrega</small>
                        <div>
                          {sale.shipping_address.street}, {sale.shipping_address.number}
                          {sale.shipping_address.complement && ` - ${sale.shipping_address.complement}`}
                        </div>
                        <div>
                          {sale.shipping_address.neighborhood} - {sale.shipping_address.city}/{sale.shipping_address.state}
                        </div>
                        <div>CEP: {sale.shipping_address.zip_code}</div>
                      </div>
                    )}

                    <div className="total-amount mt-4 p-3 bg-success text-white rounded text-center">
                      <small>Total a Pagar</small>
                      <span className="d-block" style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                        {CurrencyFormatter.centsToFormatted(sale?.total_amount || 0)}
                      </span>
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              {/* Coluna Direita: Forma de Pagamento */}
              <Col lg={6} className="mb-4">
                <Card className="shadow-sm h-100">
                  <Card.Header className="bg-light">
                    <Icon icon="mdi:credit-card-outline" className="me-2" />
                    <strong>Forma de Pagamento</strong>
                  </Card.Header>
                  <Card.Body>
                    <form data-pagarmecheckout-form onSubmit={(e) => e.preventDefault()}>
                      {/* PIX */}
                      {sale?.available_payment_methods?.pix && (
                        <div
                          className="payment-option mb-3"
                          onClick={() => setPaymentMethod('pix')}
                          style={{ cursor: 'pointer' }}
                        >
                          <Form.Check
                            type="radio"
                            id="payment-pix"
                            name="payment_method"
                            value="pix"
                            checked={paymentMethod === 'pix'}
                            onChange={(e) => setPaymentMethod(e.target.value)}
                            label={
                              <div>
                                <div className="d-flex align-items-center">
                                  <Icon icon="mdi:qrcode" style={{ fontSize: '28px', marginRight: '12px' }} />
                                  <div>
                                    <strong style={{ fontSize: '1.05rem' }}>PIX</strong>
                                    <div style={{ fontSize: '0.85rem', color: '#6c757d', marginTop: '2px' }}>
                                      Aprovação instantânea
                                    </div>
                                  </div>
                                </div>
                              </div>
                            }
                          />
                        </div>
                      )}

                      {/* Cartão de Crédito */}
                      {sale?.available_payment_methods?.credit_card && (
                        <>
                          <div
                            className="payment-option mb-3"
                            onClick={() => setPaymentMethod('credit_card')}
                            style={{ cursor: 'pointer' }}
                          >
                            <Form.Check
                              type="radio"
                              id="payment-credit"
                              name="payment_method"
                              value="credit_card"
                              checked={paymentMethod === 'credit_card'}
                              onChange={(e) => setPaymentMethod(e.target.value)}
                              label={
                                <div>
                                  <div className="d-flex align-items-center">
                                    <Icon icon="mdi:credit-card" style={{ fontSize: '28px', marginRight: '12px' }} />
                                    <div>
                                      <strong style={{ fontSize: '1.05rem' }}>Cartão de Crédito</strong>
                                      <div style={{ fontSize: '0.85rem', color: '#6c757d', marginTop: '2px' }}>
                                        Parcele em até {maxInstallments}x sem juros
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              }
                            />
                          </div>

                          {paymentMethod === 'credit_card' && (
                            <div className="card-fields-container mb-4">
                              <Form.Group className="mb-3">
                                <Form.Label>
                                  <Icon icon="mdi:credit-card-outline" className="me-2" />
                                  Número do Cartão
                                </Form.Label>
                                <Form.Control
                                  type="text"
                                  data-pagarmecheckout-element="number"
                                  placeholder="0000 0000 0000 0000"
                                  maxLength="19"
                                  required
                                />
                              </Form.Group>

                              <Form.Group className="mb-3">
                                <Form.Label>
                                  <Icon icon="mdi:account" className="me-2" />
                                  Nome do Titular
                                </Form.Label>
                                <Form.Control
                                  type="text"
                                  data-pagarmecheckout-element="holder_name"
                                  placeholder="Nome impresso no cartão"
                                  required
                                />
                              </Form.Group>

                              <Row>
                                <Col md={6}>
                                  <Form.Group className="mb-3">
                                    <Form.Label>Validade</Form.Label>
                                    <div className="d-flex gap-2 align-items-center">
                                      <Form.Control
                                        type="text"
                                        data-pagarmecheckout-element="exp_month"
                                        placeholder="MM"
                                        maxLength="2"
                                        style={{ width: '70px' }}
                                        required
                                      />
                                      <span>/</span>
                                      <Form.Control
                                        type="text"
                                        data-pagarmecheckout-element="exp_year"
                                        placeholder="AAAA"
                                        maxLength="4"
                                        style={{ width: '90px' }}
                                        required
                                      />
                                    </div>
                                  </Form.Group>
                                </Col>
                                <Col md={6}>
                                  <Form.Group className="mb-3">
                                    <Form.Label>
                                      <Icon icon="mdi:lock" className="me-2" />
                                      CVV
                                    </Form.Label>
                                    <Form.Control
                                      type="text"
                                      data-pagarmecheckout-element="cvv"
                                      placeholder="123"
                                      maxLength="4"
                                      style={{ width: '90px' }}
                                      required
                                    />
                                  </Form.Group>
                                </Col>
                              </Row>

                              <Form.Group className="mb-3">
                                <Form.Label>Parcelas</Form.Label>
                                <Form.Select
                                  value={installments}
                                  onChange={(e) => setInstallments(parseInt(e.target.value))}
                                >
                                  {[...Array(maxInstallments)].map((_, i) => {
                                    const parcela = i + 1;
                                    const valor = sale?.total_amount / parcela / 100;
                                    return (
                                      <option key={parcela} value={parcela}>
                                        {parcela}x de R$ {valor.toFixed(2).replace('.', ',')}
                                        {parcela === 1 ? ' à vista' : ''}
                                      </option>
                                    );
                                  })}
                                </Form.Select>
                              </Form.Group>
                            </div>
                          )}
                        </>
                      )}

                      {/* Boleto */}
                      {sale?.available_payment_methods?.boleto && (
                        <div
                          className="payment-option mb-3"
                          onClick={() => setPaymentMethod('boleto')}
                          style={{ cursor: 'pointer' }}
                        >
                          <Form.Check
                            type="radio"
                            id="payment-boleto"
                            name="payment_method"
                            value="boleto"
                            checked={paymentMethod === 'boleto'}
                            onChange={(e) => setPaymentMethod(e.target.value)}
                            label={
                              <div>
                                <div className="d-flex align-items-center">
                                  <Icon icon="mdi:barcode" style={{ fontSize: '28px', marginRight: '12px' }} />
                                  <div>
                                    <strong style={{ fontSize: '1.05rem' }}>Boleto Bancário</strong>
                                    <div style={{ fontSize: '0.85rem', color: '#6c757d', marginTop: '2px' }}>
                                      Vencimento em 3 dias úteis
                                    </div>
                                  </div>
                                </div>
                              </div>
                            }
                          />
                        </div>
                      )}
                    </form>

                    {/* Botão de Pagamento */}
                    <Button
                      variant="primary"
                      size="lg"
                      className="w-100 mt-4"
                      onClick={handlePayment}
                      disabled={processing || !paymentMethod}
                    >
                      {processing ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Processando...
                        </>
                      ) : (
                        <>
                          <Icon icon="mdi:lock-check" className="me-2" />
                          Finalizar Pagamento
                        </>
                      )}
                    </Button>

                    <div className="text-center mt-3">
                      <small className="text-muted">
                        <Icon icon="mdi:shield-check" className="me-1" />
                        Compra 100% Segura
                      </small>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>

      {/* Modal PIX */}
      {showPixModal && pixData && (
        <div className="payment-modal-overlay" onClick={() => {}}>
          <div className="payment-modal" onClick={(e) => e.stopPropagation()}>
            <div className="payment-modal-header">
              <span style={{ fontSize: '1.3rem', fontWeight: 'bold' }}>
                <Icon icon="mdi:qrcode" className="me-2" />
                Pagamento via PIX
              </span>
            </div>
            <div className="payment-modal-body text-center">
              <p className="mb-3">Escaneie o QR Code ou copie o código para pagar:</p>

              <div className="mb-4">
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(pixData.qr_code)}`}
                  alt="QR Code PIX"
                  style={{ width: '200px', height: '200px', border: '4px solid #e9ecef', borderRadius: '12px' }}
                />
              </div>

              <div className="mb-3">
                <small className="text-muted d-block mb-2">Código PIX (Copia e Cola):</small>
                <div className="d-flex gap-2">
                  <Form.Control
                    type="text"
                    value={pixData.qr_code}
                    readOnly
                    style={{ fontSize: '0.8rem' }}
                  />
                  <Button
                    variant={pixCopied ? 'success' : 'outline-primary'}
                    onClick={handleCopyPix}
                    style={{ minWidth: '100px' }}
                  >
                    <Icon icon={pixCopied ? 'mdi:check' : 'mdi:content-copy'} className="me-1" />
                    {pixCopied ? 'Copiado!' : 'Copiar'}
                  </Button>
                </div>
              </div>

              <div className="d-flex align-items-center justify-content-center gap-2 mt-4 mb-2">
                <Spinner animation="border" size="sm" variant="primary" />
                <span className="text-primary" style={{ fontWeight: '500' }}>Aguardando confirmação do pagamento...</span>
              </div>

              {pixData.expires_at && (
                <small className="text-muted">
                  Expira em: {new Date(pixData.expires_at).toLocaleString('pt-BR')}
                </small>
              )}

              <div className="mt-3">
                <small className="text-muted">
                  Valor: <strong>{CurrencyFormatter.centsToFormatted(pixData.total || sale?.total_amount || 0)}</strong>
                </small>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Boleto */}
      {showBoletoModal && boletoData && (
        <div className="payment-modal-overlay" onClick={() => {}}>
          <div className="payment-modal" onClick={(e) => e.stopPropagation()}>
            <div className="payment-modal-header">
              <span style={{ fontSize: '1.3rem', fontWeight: 'bold' }}>
                <Icon icon="mdi:barcode" className="me-2" />
                Pagamento via Boleto
              </span>
            </div>
            <div className="payment-modal-body">
              <p className="mb-3">Pague o boleto até a data de vencimento:</p>

              {boletoData.due_date && (
                <div className="p-2 bg-light rounded mb-3 text-center">
                  <strong>Vencimento: </strong>
                  {new Date(boletoData.due_date).toLocaleDateString('pt-BR')}
                </div>
              )}

              <div className="mb-3">
                <small className="text-muted d-block mb-2">Código de Barras:</small>
                <div className="d-flex gap-2">
                  <Form.Control
                    type="text"
                    value={boletoData.barcode}
                    readOnly
                    style={{ fontSize: '0.8rem' }}
                  />
                  <Button
                    variant={boletoCopied ? 'success' : 'outline-primary'}
                    onClick={handleCopyBoleto}
                    style={{ minWidth: '100px' }}
                  >
                    <Icon icon={boletoCopied ? 'mdi:check' : 'mdi:content-copy'} className="me-1" />
                    {boletoCopied ? 'Copiado!' : 'Copiar'}
                  </Button>
                </div>
              </div>

              {boletoData.url && (
                <div className="text-center mb-3">
                  <a
                    href={boletoData.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-outline-primary"
                  >
                    <Icon icon="mdi:download" className="me-2" />
                    Baixar Boleto PDF
                  </a>
                </div>
              )}

              {boletoData.instructions && (
                <div className="p-2 bg-light rounded text-center">
                  <small className="text-muted">{boletoData.instructions}</small>
                </div>
              )}

              <div className="text-center mt-3">
                <Button
                  variant="primary"
                  onClick={() => {
                    setShowBoletoModal(false);
                    navigate('/checkout/success', {
                      state: {
                        order: {
                          id: boletoData.sale_id,
                          payment_method: 'boleto',
                          total_amount: sale?.total_amount,
                          product_name: sale?.product_name,
                          customer_name: sale?.customer_name
                        }
                      }
                    });
                  }}
                >
                  Entendi, vou pagar o boleto
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SaleCheckoutPaymentPage;
